import { useEffect, useMemo, useState } from 'react';

import { useMachine } from '@xstate/react';
import { fromPromise } from 'xstate';

import { createContentfulContentTypeEntriesMachine } from './machine';

import type { ContentfulClientApi, EntryCollection } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns Contentful entries for the provided content type.
 *
 * Additionally, returns `fetching` and `error` states.
 *
 * @example
 *
 * const { data, fetching, error } = useContentfulContentTypeEntries({
 *   contentType: 'contentfulContentTypeID',
 * });
 * const [firstEntry, secondEntry] = data ?? [];
 *
 * const { fields, tags } = firstEntry ?? {};
 * const { image, bodyText, headingText } = fields ?? {};
 * const hasSuperTag = tags?.includes('SUPER');
 *
 * const { headingText } = secondEntry.fields ?? {};
 *
 * @see {@link https://www.contentful.com/developers/docs/javascript/tutorials/using-js-cda-sdk/#retrieve-all-entries-of-a-space Contentful Docs}
 */
export const useContentfulContentTypeEntriesFactory = <ContentTypeFields>(
  params: UseContentfulContentTypeEntriesFactoryParams,
) => {
  const {
    contentfulClient,
    isPreviewModeEnabled,
    limit,
    order = 'updated-at',
    include,
    contentType,
    tags = [],
    pause = false,
    ttl = DEFAULT_TTL,
    searchParams,
  } = params;

  const isContentfulClientAvailable = contentfulClient !== undefined;

  const queryOrder = order === 'updated-at' ? '-sys.updatedAt' : '-sys.createdAt';

  // ─── Query ───────────────────────────────────────────────────────────

  const tagsString = tags.toString();

  // NOTE: Use only primitive and/or memoized non-primitive values in this list to avoid
  //       an infinite data fetching loop.
  const queryInput = useMemo<ContentfulQueryInput>(() => {
    return {
      content_type: contentType,
      include,
      limit,
      order: queryOrder,
      ...(tagsString ? { 'metadata.tags.sys.id[in]': tagsString } : {}),
      ...searchParams,
    };
  }, [contentType, searchParams, include, limit, queryOrder, tagsString]);

  // ─── Machine ─────────────────────────────────────────────────────────

  const shouldPreventGetEntries = !isContentfulClientAvailable || pause;

  const [machine] = useState(createContentfulContentTypeEntriesMachine<ContentTypeFields>);

  const machineWithContentfulClient = useMemo(
    () =>
      machine.provide({
        actors: {
          getEntries: fromPromise<EntryCollection<ContentTypeFields> | undefined>(
            async ({ input }) => contentfulClient?.getEntries<ContentTypeFields>(input),
          ),
        },
      }),
    [contentfulClient, machine],
  );

  const [state, send] = useMachine(machineWithContentfulClient, {
    input: {
      isPreviewMode: isPreviewModeEnabled,
      error: isContentfulClientAvailable
        ? undefined
        : new Error('Contentful client was not found!'),
    },
  });

  // Get entries when the query input changes.
  useEffect(() => {
    if (shouldPreventGetEntries) return;

    send({
      type: 'GET_ENTRIES',
      input: { query: queryInput, ttl },
    });
  }, [queryInput, shouldPreventGetEntries, ttl, send]);

  // ─────────────────────────────────────────────────────────────────────

  const isFetching = shouldPreventGetEntries
    ? false
    : state.matches('waiting') ||
      state.matches('searching') ||
      state.matches('reading') ||
      state.matches('fetching');

  return {
    data: state.context.data,
    error: state.context.error,
    fetching: isFetching,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_TTL = 15 * 60 * 1000; // 15 minutes

// ─── Types ───────────────────────────────────────────────────────────────────

type UseContentfulContentTypeEntriesFactoryParams = {
  contentfulClient: ContentfulClientApi | undefined;
  isPreviewModeEnabled?: boolean;

  contentType: string;

  /**
   * Depth of linked entries.
   */
  include?: number;
  limit?: number;
  order?: 'created-at' | 'updated-at';
  pause?: boolean;
  tags?: readonly string[];

  /**
   * Extra search parameters to narrow down the search results.
   *
   * NOTE: Always "memoize" the parameters object to avoid infinite loops.
   *
   * @example
   * {
   *   searchParams: {
   *     'fields.<field-name>': '<value>',
   *     'fields.<field-name>[within]': '1,2,3,4,5',
   *   }
   * }
   *
   * @see {@link https://www.contentful.com/developers/docs/references/content-delivery-api/#/reference/search-parameters/equality-operator}
   */
  searchParams?: Record<string, string | number | boolean>;

  /**
   * TTL - Time (in milliseconds) to live
   * Specifies how long the data should be cached.
   *
   * @default 60 * 60 * 1000
   */
  ttl?: number;
};

type ContentfulQueryInput = Readonly<{
  content_type: string;

  /**
   * Depth of linked entries.
   */
  include?: number;
  limit?: number;
  order?: '-sys.updatedAt' | '-sys.createdAt';
  'metadata.tags.sys.id[in]'?: string;
}>;
